import { FingerprintAIO } from "@awesome-cordova-plugins/fingerprint-aio";
import { Capacitor } from "@capacitor/core";
import { trackErrorEvent, trackEvent } from "../utils/analytics";
import { AppPlatform } from "../utils/models";
import { storageService } from "./storageService.service";

export interface BiometricCredentials {
	email: string;
	password: string;
	token?: string;
}

enum BiometricType {
	Biometric = "biometric",
	Face = "face",
	Finger = "finger",
}

export class BiometricService {
	private readonly CREDENTIALS_KEY = "user_credentials";

	private isNativePlatform(): boolean {
		return Capacitor.isNativePlatform();
	}

	async hasBiometricHardware(): Promise<boolean> {
		try {
			if (!this.isNativePlatform()) {
				return false;
			}

			const result = await FingerprintAIO.isAvailable({
				allowBackup: true,
				requireStrongBiometrics: true,
			});

			return result === BiometricType.Biometric || result === BiometricType.Face || result === BiometricType.Finger;
		} catch (error) {
			return false;
		}
	}

	async getBiometricType(): Promise<string | null> {
		try {
			if (!this.isNativePlatform()) {
				return null;
			}

			const result = await FingerprintAIO.isAvailable({
				allowBackup: true,
				requireStrongBiometrics: true,
			});
			const platform = Capacitor.getPlatform();

			switch (result) {
				case BiometricType.Face:
					return platform === AppPlatform.IOS ? "Face ID" : "Face Recognition";
				case BiometricType.Finger:
					return platform === AppPlatform.IOS ? "Touch ID" : "Fingerprint";
				case BiometricType.Biometric:
					return "Biometric";
				default:
					return null;
			}
		} catch (error) {
			return null;
		}
	}

	async isBiometricAvailable(): Promise<boolean> {
		try {
			if (!this.isNativePlatform()) {
				return false;
			}

			const result = await FingerprintAIO.isAvailable({
				allowBackup: true,
				requireStrongBiometrics: true,
			});

			const isAvailable = [BiometricType.Biometric, BiometricType.Face, BiometricType.Finger].includes(result as BiometricType);

			return isAvailable;
		} catch (error) {
			trackErrorEvent("isBiometricAvailable failed", error, {
				platform: Capacitor.getPlatform(),
			});
			return false;
		}
	}

	async hasSavedCredentials(): Promise<boolean> {
		try {
			if (!this.isNativePlatform()) {
				return false;
			}

			const credentials = await storageService.get(this.CREDENTIALS_KEY);
			const hasCredentials = !!credentials;
			return hasCredentials;
		} catch (error) {
			trackErrorEvent("hasSavedCredentials failed", error, {
				platform: Capacitor.getPlatform(),
			});
			return false;
		}
	}

	async saveCredentials(credentials: BiometricCredentials): Promise<boolean> {
		try {
			if (!this.isNativePlatform()) {
				trackEvent("saveCredentials not native platform", {
					platform: Capacitor.getPlatform(),
				});
				return false;
			}

			const credentialsString = JSON.stringify(credentials);
			const success = await storageService.set(this.CREDENTIALS_KEY, credentialsString);

			if (success) {
				trackEvent("biometric credentials saved", {
					storageMethod: storageService.getStorageMethod(),
				});
			} else {
				trackEvent("biometric credentials not persisted", {
					warning: "Storage unavailable - credentials not saved",
				});
			}

			return success;
		} catch (error) {
			trackErrorEvent("saveCredentials failed", error, {
				platform: Capacitor.getPlatform(),
			});
			return false;
		}
	}

	async getCredentials(): Promise<BiometricCredentials | null> {
		try {
			if (!this.isNativePlatform()) {
				return null;
			}

			// First authenticate with biometrics
			await this.authenticateWithBiometrics();

			// Then retrieve credentials
			const credentialsString = await storageService.get(this.CREDENTIALS_KEY);

			if (!credentialsString) {
				return null;
			}

			return JSON.parse(credentialsString);
		} catch (error) {
			trackErrorEvent("getCredentials failed", error, {
				platform: Capacitor.getPlatform(),
			});
			return null;
		}
	}

	async authenticateWithBiometrics(): Promise<void> {
		if (!this.isNativePlatform()) {
			throw new Error("Biometric authentication only available on native platforms");
		}

		const platform = Capacitor.getPlatform();
		const config = {
			title: platform === AppPlatform.IOS ? "Biometric Authentication" : "Authentication Required",
			subtitle: platform === AppPlatform.IOS ? "Please authenticate" : "Use your biometric to continue",
			fallbackButtonTitle: platform === AppPlatform.IOS ? "Use Passcode" : "Use PIN",
			disableBackup: false,
		};

		try {
			await FingerprintAIO.show(config);
		} catch (error) {
			trackErrorEvent("biometric authentication failed", error, {
				platform: Capacitor.getPlatform(),
			});
			throw error;
		}
	}

	async clearCredentials(): Promise<void> {
		try {
			if (!this.isNativePlatform()) {
				return;
			}

			await storageService.remove(this.CREDENTIALS_KEY);
			trackEvent("biometric credentials cleared");
		} catch (error) {
			trackErrorEvent("clearCredentials failed", error, {
				platform: Capacitor.getPlatform(),
			});
			throw error;
		}
	}

	async updateToken(token: string): Promise<void> {
		try {
			if (!this.isNativePlatform()) {
				return;
			}

			const credentialsString = await storageService.get(this.CREDENTIALS_KEY);

			if (credentialsString) {
				const credentials = JSON.parse(credentialsString);
				credentials.token = token;
				await this.saveCredentials(credentials);
			}
		} catch (error) {
			trackErrorEvent("updateToken failed", error, {
				platform: Capacitor.getPlatform(),
			});
			throw error;
		}
	}

	async getPlatformInfo(): Promise<{
		platform: string;
		isNative: boolean;
		biometricAvailable: boolean;
		biometricType: string | null;
		hasCredentials: boolean;
	}> {
		const platform = Capacitor.getPlatform();
		const isNative = this.isNativePlatform();
		const biometricAvailable = await this.isBiometricAvailable();
		const hasCredentials = await this.hasSavedCredentials();

		let biometricType = null;
		if (biometricAvailable) {
			try {
				const result = await FingerprintAIO.isAvailable({
					allowBackup: true,
					requireStrongBiometrics: true,
				});
				biometricType = result;
			} catch (error) {
				trackErrorEvent("Error getting biometric type", error, { platform });
			}
		}

		const info = {
			platform,
			isNative,
			biometricAvailable,
			biometricType,
			hasCredentials,
		};

		return info;
	}
}

export const biometricService = new BiometricService();
